import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { CheckCircle, Loader2, XCircle } from "lucide-react"
import { changePassword } from "../../../lib/api/api"
import i18n from "../../../i18n"

// Mirrors the server policy (src/utils/password.py); the server has the final word.
export const PASSWORD_MIN_LENGTH = 10

export function localPasswordProblem(newPassword: string, confirm: string, email?: string): string | null {
  if (newPassword.length < PASSWORD_MIN_LENGTH) {
    return i18n.t('settings.password.errors.tooShort', { minLength: PASSWORD_MIN_LENGTH })
  }
  if (/^\d+$/.test(newPassword)) {
    return i18n.t('settings.password.errors.onlyDigits')
  }
  if (email && newPassword.trim().toLowerCase() === email.trim().toLowerCase()) {
    return i18n.t('settings.password.errors.matchesEmail')
  }
  if (newPassword !== confirm) {
    return i18n.t('settings.password.errors.mismatch')
  }
  return null
}

function errorDetail(error: any, fallback: string): string {
  const status = error?.response?.status
  const detail = error?.response?.data?.detail
  if (typeof detail === "string" && detail) return detail
  if (Array.isArray(detail) && detail.length) return detail.map((d: any) => d?.msg || String(d)).join(", ")
  if (status === 401) return "Sessione scaduta: effettua di nuovo l'accesso."
  return fallback
}

const inputClass =
  "w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"

export default function ChangePasswordForm({ email }: { email?: string }) {
  const { t } = useTranslation()
  const [current, setCurrent] = useState("")
  const [next, setNext] = useState("")
  const [confirm, setConfirm] = useState("")
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccess(null)
    if (!current) {
      setError(t('settings.password.errors.missingCurrent'))
      return
    }
    const problem = localPasswordProblem(next, confirm, email)
    if (problem) {
      setError(problem)
      return
    }
    setSaving(true)
    try {
      await changePassword(current, next)
      setCurrent("")
      setNext("")
      setConfirm("")
      setSuccess(t('settings.password.success'))
    } catch (err: any) {
      setError(errorDetail(err, t('settings.password.errors.changeFailed')))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="max-w-md">
      <h2 className="text-xl font-semibold mb-1">{t('settings.password.title')}</h2>
      <p className="text-sm text-gray-500 mb-4">
        {t('settings.password.hint', { minLength: PASSWORD_MIN_LENGTH })}
      </p>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center" role="alert">
          <XCircle className="w-5 h-5 text-red-500 mr-2 flex-shrink-0" />
          <span className="text-red-700 text-sm">{error}</span>
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center" role="status">
          <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" />
          <span className="text-green-700 text-sm">{success}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <div>
          <label htmlFor="current-password" className="block text-sm font-medium text-gray-700 mb-1">
            {t('settings.password.currentLabel')}
          </label>
          <input
            id="current-password"
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 mb-1">
            {t('settings.password.newLabel')}
          </label>
          <input
            id="new-password"
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 mb-1">
            {t('settings.password.confirmLabel')}
          </label>
          <input
            id="confirm-password"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className={inputClass}
          />
        </div>
        <button
          type="submit"
          disabled={saving}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center"
        >
          {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          {t('settings.password.submit')}
        </button>
      </form>
    </div>
  )
}
